/**
 * Counts active filters per section from a filter snapshot
 *
 * Pure function that computes per-section filter counts from draft
 * or committed filter state. Used by useFilterPanelLogic to provide
 * section badge counts ("BASIC (3)", "FINANCIAL (1)", etc.).
 * Multi-select filters count each selected item individually;
 * range filters count as 0 or 1 (active/inactive).
 * @module features/scouting/utils/countSectionFilters
 */

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";

import type { FilterSnapshot } from "@/hooks/useFilterState";

import countActiveFilters from "./countActiveFilters";

/** Section key identifiers for filter panel sections */
export type SectionKey =
  | "basic"
  | "financial"
  | "fitAndRatings"
  | "performance"
  | "goalkeeper"
  | "reportStats";

/**
 * Computes the number of active filters in each section.
 *
 * Multi-select filters (roles, positions, clubs, etc.) count each
 * selected item as 1. Range filters count as 0 or 1 based on
 * whether they differ from their default values. Custom stat ranges
 * are pruned against available metric IDs to prevent orphaned ranges
 * (from deleted/renamed metrics) from inflating the count.
 * @param draft - Filter snapshot to count from
 * @param availableMetricIds - IDs of currently available custom metrics
 * @returns Per-section active filter counts
 */
function countSectionFilters(
  draft: FilterSnapshot,
  availableMetricIds: ReadonlySet<string> = new Set()
): Record<SectionKey, number> {
  return {
    basic:
      draft.selectedRoleArchetypes.length +
      draft.selectedPositionIds.length +
      draft.selectedClubIds.length +
      draft.selectedLeagueIds.length +
      draft.selectedCompetitionIds.length +
      draft.selectedNationalityIds.length +
      countActiveFilters(
        isRangeActive(draft.ageRange, DEFAULT_AGE_RANGE),
        isRangeActive(draft.contractMonthsRange, DEFAULT_CONTRACT_MONTHS_RANGE)
      ),
    financial: countActiveFilters(
      isRangeActive(draft.valuationRange, DEFAULT_VALUATION_RANGE)
    ),
    fitAndRatings: countActiveFilters(
      isRangeActive(draft.gprRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.fitScoreRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.teamStyleFitRange, DEFAULT_SCORE_RANGE)
    ),
    performance: countActiveFilters(
      isRangeActive(draft.carryingRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.dribblingRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.passingRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.shootingRange, DEFAULT_SCORE_RANGE)
    ),
    goalkeeper: countActiveFilters(
      isRangeActive(draft.gkPassingRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.gkShotStoppingRange, DEFAULT_SCORE_RANGE),
      isRangeActive(draft.gkPositioningRange, DEFAULT_SCORE_RANGE)
    ),
    reportStats: Object.entries(draft.customStatRanges).filter(
      ([key, range]) =>
        availableMetricIds.has(key) &&
        isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
    ).length,
  };
}

export default countSectionFilters;
